import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const inp = "mt-1.5 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass focus:outline-none transition-colors";
const Field = ({ label, testId, ...p }) => (
  <div><label className="text-sm text-[#9CA3AF]">{label}</label><input data-testid={testId} required className={inp} {...p} /></div>
);

const SignIn = () => {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await login(f.email.trim(), f.password); } catch (x) { setErr(formatApiError(x.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="glimpse-signin-form">
      <Field label="Email" testId="glimpse-login-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <Field label="Password" testId="glimpse-login-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <div className="-mt-2 text-right"><Link to="/forgot-password" className="text-sm cg-link">Forgot password?</Link></div>
      {err && <p data-testid="glimpse-login-error" className="text-sm text-terracotta">{err}</p>}
      <button data-testid="glimpse-login-submit" disabled={busy} className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">{busy ? "Signing in…" : "Sign In"}</button>
    </form>
  );
};

const Register = () => {
  const { register } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [marketing, setMarketing] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (f.password !== f.confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await register({ first_name: f.first_name.trim(), last_name: f.last_name.trim(), email: f.email.trim(), password: f.password });
      if (marketing) await api.post("/glimpse/marketing-consent", { opt_in: true }).catch(() => {});
    } catch (x) { setErr(formatApiError(x.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="glimpse-register-form">
      <div className="grid grid-cols-2 gap-4">
        <Field label="First name" testId="glimpse-reg-firstname" value={f.first_name} onChange={set("first_name")} />
        <Field label="Last name" testId="glimpse-reg-lastname" value={f.last_name} onChange={set("last_name")} />
      </div>
      <Field label="Email address" testId="glimpse-reg-email" type="email" value={f.email} onChange={set("email")} />
      <Field label="Password (min 8 characters)" testId="glimpse-reg-password" type="password" value={f.password} onChange={set("password")} />
      <Field label="Confirm password" testId="glimpse-reg-confirm" type="password" value={f.confirm} onChange={set("confirm")} />
      <p className="text-xs text-[#9CA3AF] leading-relaxed" data-testid="glimpse-privacy-notice">
        We collect your name, email and password to create and manage your account, deliver your assessment results and support the service. Your results are private to you. <a href="/#privacy" className="cg-link">Privacy</a>
      </p>
      <label className="flex items-start gap-3 text-sm text-[#B9BDC7] cursor-pointer">
        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="glimpse-marketing-checkbox" className="mt-1 accent-[#D9A25F]" />
        <span>Optional: send me occasional updates and news from Caesar's Ghost. <span className="text-[#6B7280]">Not required for the free Authority Glimpse.</span></span>
      </label>
      {err && <p data-testid="glimpse-register-error" className="text-sm text-terracotta">{err}</p>}
      <button data-testid="glimpse-register-submit" disabled={busy} className="w-full bg-brass text-obsidian font-medium py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">{busy ? "Creating…" : "Create Account"}</button>
    </form>
  );
};

export const GlimpseAuth = () => {
  const [mode, setMode] = useState("register");
  const tab = (m, label) => (
    <button type="button" onClick={() => setMode(m)} data-testid={`glimpse-tab-${m}`}
      className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${mode === m ? "bg-brass text-obsidian font-medium" : "text-[#9CA3AF] hover:text-[#F3F4F6]"}`}>{label}</button>
  );
  return (
    <div className="max-w-md mx-auto px-5 pt-14 pb-16 cg-fade-up" data-testid="glimpse-auth">
      <p className="cg-eyebrow mb-3">Free · Authority Glimpse</p>
      <h1 className="font-serif text-4xl text-[#F3F4F6] mb-3">Start your Authority Glimpse</h1>
      <p className="text-sm text-[#9CA3AF] mb-6">Create a free account, or sign in if you already have one. No organization code needed.</p>
      <div className="cg-card p-7">
        <div className="flex gap-1 p-1 mb-6 rounded-full bg-[#0D0E12] border border-[var(--cg-border)]">{tab("register", "Create account")}{tab("signin", "Sign in")}</div>
        {mode === "register" ? <Register /> : <SignIn />}
      </div>
    </div>
  );
};
